import api from './api';
import type { IBlog } from '../types/blog'; // We will create this type next

// --- Interfaces for function payloads ---
interface CommentPayload {
  content: string;
}

// --- Public Functions ---

/**
 * Service to get all published blog posts.
 */
const getAllPublishedBlogs = async (): Promise<IBlog[]> => {
  try {
    const response = await api.get<IBlog[]>('/blogs');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch blogs.');
  }
};

/**
 * Service to get a single blog post by its slug.
 */
const getBlogBySlug = async (slug: string): Promise<IBlog> => {
  try {
    const response = await api.get<IBlog>(`/blogs/${slug}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch blog post.');
  }
};

/**
 * Service to get all published blogs that have a specific tag.
 */
const getBlogsByTag = async (tag: string): Promise<IBlog[]> => {
    try {
      const response = await api.get<IBlog[]>(`/blogs/tags/${tag}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch blogs by tag.');
    }
  };

// --- Admin/Mentor Functions (CRUD) ---

/**
 * Service to get ALL blogs (published and unpublished) for the admin dashboard.
 */
const getAllBlogsForAdmin = async (): Promise<IBlog[]> => {
    try {
      const response = await api.get<IBlog[]>('/blogs/all');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch blogs for admin.');
    }
  };

/**
 * Service to create a new blog post.
 */
const createBlog = async (payload: Partial<IBlog>): Promise<IBlog> => {
  try {
    const response = await api.post<{ blog: IBlog }>('/blogs', payload);
    return response.data.blog;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create blog post.');
  }
};

/**
 * Service to update an existing blog post.
 */
const updateBlog = async (id: string, payload: Partial<IBlog>): Promise<IBlog> => {
    try {
      const response = await api.put<{ blog: IBlog }>(`/blogs/${id}`, payload);
      return response.data.blog;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update blog post.');
    }
  };

/**
 * Service to delete a blog post.
 */
const deleteBlog = async (id: string): Promise<string> => {
    try {
      const response = await api.delete<{ message: string }>(`/blogs/${id}`);
      return response.data.message;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete blog post.');
    }
  };

// --- Authenticated User Functions (Comments & Likes) ---

/**
 * Service to like or unlike a blog post.
 */
const likeBlog = async (id: string): Promise<{ likes: number }> => {
    try {
      const response = await api.post<{ likes: number }>(`/blogs/${id}/like`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to like blog post.');
    }
};

/**
 * Service to add a top-level comment to a blog post.
 */
const addComment = async (blogId: string, payload: CommentPayload): Promise<IBlog> => {
    try {
      const response = await api.post<IBlog>(`/blogs/${blogId}/comments`, payload);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add comment.');
    }
};

/**
 * Service to reply to an existing comment.
 */
const addReply = async (blogId: string, commentId: string, payload: CommentPayload): Promise<IBlog> => {
    try {
      const response = await api.post<IBlog>(`/blogs/${blogId}/comments/${commentId}/reply`, payload);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add reply.');
    }
};

/**
 * Service to delete a comment or a reply.
 */
const deleteComment = async (blogId: string, commentId: string): Promise<string> => {
    try {
      const response = await api.delete<{ message: string }>(`/blogs/${blogId}/comments/${commentId}`);
      return response.data.message;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete comment.');
    }
};

// ✨ NEW: Service to get a single blog's full data for editing
const getBlogForEdit = async (id: string): Promise<IBlog> => {
    try {
      const response = await api.get<IBlog>(`/blogs/${id}/edit`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch blog for editing.');
    }
  };


// --- Group all functions into a single object for a clean export ---
const blogService = {
    getAllPublishedBlogs,
    getBlogBySlug,
    getBlogsByTag,
    getAllBlogsForAdmin,
    createBlog,
    updateBlog,
    deleteBlog,
    likeBlog,
    addComment,
    addReply,
    deleteComment,
    getBlogForEdit
};

export default blogService;